import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import AdminLayout from '@/components/Layout';
import { Tv, Plus, Trash2, Shield, ShieldOff, CheckSquare, Square, X, Pencil, Save, Loader2, Upload, Link as LinkIcon, FileText, CheckCircle2, Pause, Play } from 'lucide-react';

export default function Channels() {
  const [channels, setChannels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [epgSources, setEpgSources] = useState([]);
  const [sources, setSources] = useState([]);
  const [transcodes, setTranscodes] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newChan, setNewChan] = useState({ name: '', category_id: '', stream_url: '', logo_url: '', epg_id: '', epg_source_id: '', source_id: '', transcode_profile_id: '' });
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // ---- Edit modal state ----
  const [editChan, setEditChan] = useState(null);   // currently-edited channel object (or null)
  const [editBusy, setEditBusy] = useState(false);
  const [editErr, setEditErr] = useState("");

  // ---- M3U import state ----
  const [showImport, setShowImport] = useState(false);
  const [importMode, setImportMode] = useState("url"); // 'url' | 'content'
  const [importUrl, setImportUrl] = useState("");
  const [importContent, setImportContent] = useState("");
  const [importDefaultCat, setImportDefaultCat] = useState("İçe Aktarılanlar");
  const [importBusy, setImportBusy] = useState(false);
  const [importErr, setImportErr] = useState("");
  const [importResult, setImportResult] = useState(null);

  const allSelected = channels.length > 0 && selected.size === channels.length;
  const someSelected = selected.size > 0 && selected.size < channels.length;

  const toggleAll = () => {
    if (allSelected || someSelected) setSelected(new Set());
    else setSelected(new Set(channels.map(c => c._id)));
  };
  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const reloadChannels = () => api.get('/channels').then(res => { setChannels(res.data); setSelected(new Set()); });

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} kanalı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) return;
    setBulkBusy(true);
    try {
      await api.post('/channels/bulk', { action: 'delete', ids: Array.from(selected) });
      await reloadChannels();
    } catch (err) { alert(err?.response?.data?.detail || 'Toplu silme başarısız.'); }
    finally { setBulkBusy(false); }
  };

  const handleBulkSetCategory = async (categoryId) => {
    if (!categoryId || selected.size === 0) return;
    setBulkBusy(true);
    try {
      await api.post('/channels/bulk', { action: 'set_category', ids: Array.from(selected), category_id: categoryId });
      await reloadChannels();
    } catch (err) { alert(err?.response?.data?.detail || 'Toplu kategori güncellemesi başarısız.'); }
    finally { setBulkBusy(false); }
  };

  const loadCategories = () =>
    api.get('/categories').then(res => setCategories(res.data)).catch(err => console.error("Kategori yükleme hatası:", err));

  const loadEpgSources = () =>
    api.get('/epg-sources').then(res => setEpgSources(res.data)).catch(err => console.error("EPG yükleme hatası:", err));

  const loadSources = () =>
    api.get('/sources').then(res => setSources(res.data)).catch(err => console.error("Source yükleme hatası:", err));

  const loadTranscodes = () =>
    api.get('/transcoding').then(res => setTranscodes(res.data)).catch(err => console.error("Transcode yükleme hatası:", err));

  useEffect(() => {
    Promise.all([
      api.get('/channels'),
      api.get('/categories'),
      api.get('/epg-sources'),
      api.get('/sources'),
      api.get('/transcoding'),
    ])
      .then(([chanRes, catRes, epgRes, srcRes, txRes]) => {
        setChannels(chanRes.data);
        setCategories(catRes.data);
        setEpgSources(epgRes.data);
        setSources(srcRes.data);
        setTranscodes(txRes.data);
      }).catch(err => console.error("Veri yükleme hatası:", err));

    // Refresh dropdown'ları kullanıcı sayfaya geri gelince — yeni eklenen
    // kayıtlar (kategori, EPG kaynağı, source, transcode profili) anında görünsün.
    const onFocus = () => { loadCategories(); loadEpgSources(); loadSources(); loadTranscodes(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const openAddModal = () => {
    // Refresh dropdowns right before opening so they reflect any newly-created
    // categories, EPG sources, sources or transcode profiles.
    loadCategories();
    loadEpgSources();
    loadSources();
    loadTranscodes();
    setShowAddModal(true);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      // Boş string'leri null'a çevir ki Pydantic Optional[str] olarak kabul etsin.
      const payload = {
        ...newChan,
        epg_source_id:        newChan.epg_source_id || null,
        source_id:            newChan.source_id || null,
        transcode_profile_id: newChan.transcode_profile_id || null,
      };
      await api.post('/channels', payload);
      setShowAddModal(false);
      api.get('/channels').then(res => setChannels(res.data));
      setNewChan({ name: '', category_id: '', stream_url: '', logo_url: '', epg_id: '', epg_source_id: '', source_id: '', transcode_profile_id: '' });
      toast.success('Kanal eklendi');
    } catch (err) { toast.error(err?.response?.data?.detail || "Kanal eklenemedi."); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu kanalı silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/channels/${id}`);
      setChannels(channels.filter(c => c._id !== id));
      toast.success('Kanal silindi');
    } catch (err) { toast.error("Silinemedi."); }
  };

  // Pause ↔ Resume — flip status between 'online' and 'offline' without opening the edit modal.
  // Also busts the in-memory restream manifest cache so paused channels
  // actually stop serving within seconds.
  const togglePause = async (chan) => {
    const nextStatus = chan.status === 'online' ? 'offline' : 'online';
    try {
      const res = await api.put(`/channels/${chan._id}`, { status: nextStatus });
      const updated = res.data;
      setChannels(prev => prev.map(c => (c._id === chan._id ? { ...c, ...updated } : c)));
      toast.success(nextStatus === 'offline' ? 'Kanal duraklatıldı' : 'Kanal yayında');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'İşlem başarısız.');
    }
  };

  // ---- Edit ----
  const openEdit = (chan) => {
    setEditErr("");
    setEditChan({
      _id:                  chan._id,
      name:                 chan.name || "",
      stream_url:           chan.stream_url || "",
      logo_url:             chan.logo_url || "",
      epg_id:               chan.epg_id || "",
      category_id:          chan.category_id || "",
      epg_source_id:        chan.epg_source_id || "",
      source_id:            chan.source_id || "",
      transcode_profile_id: chan.transcode_profile_id || "",
    });
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!editChan) return;
    setEditBusy(true); setEditErr("");
    const { _id, ...patch } = editChan;
    try {
      const res = await api.put(`/channels/${_id}`, patch);
      const updated = res.data;
      // Optimistic refresh: only the affected row in state is replaced.
      setChannels(prev => prev.map(c => (c._id === _id ? { ...c, ...updated } : c)));
      setEditChan(null);
      toast.success('Kanal güncellendi');
    } catch (err) {
      const msg = err?.response?.data?.detail || "Güncelleme başarısız.";
      setEditErr(msg);
      toast.error(msg);
    } finally { setEditBusy(false); }
  };

  // ---- M3U Import ----
  const openImport = () => {
    setImportMode("url");
    setImportUrl("");
    setImportContent("");
    setImportDefaultCat("İçe Aktarılanlar");
    setImportErr("");
    setImportResult(null);
    setShowImport(true);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setImportErr("Dosya 10 MB sınırını aşıyor.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImportContent(String(ev.target.result || ""));
      setImportMode("content");
      setImportErr("");
    };
    reader.readAsText(file);
  };

  const handleImport = async (e) => {
    e.preventDefault();
    setImportBusy(true); setImportErr(""); setImportResult(null);
    try {
      const body = { default_category: importDefaultCat || "İçe Aktarılanlar" };
      if (importMode === "url") {
        if (!importUrl.trim()) { setImportErr("M3U URL'si gerekli."); setImportBusy(false); return; }
        body.url = importUrl.trim();
      } else {
        if (!importContent.trim()) { setImportErr("M3U içeriği boş."); setImportBusy(false); return; }
        body.content = importContent;
      }
      const res = await api.post("/channels/import-m3u", body);
      setImportResult(res.data);
      // Refresh channels + categories
      await reloadChannels();
      await loadCategories();
      const r = res.data;
      toast.success(`İçe aktarım: ${r.inserted} eklendi, ${r.updated} güncellendi`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "İçe aktarım başarısız.";
      setImportErr(msg);
      toast.error(msg);
    } finally { setImportBusy(false); }
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-white flex items-center gap-3" data-testid="channels-page-title">
            <Tv className="text-amber-500" /> Kanal Yönetimi
          </h1>
          <div className="flex items-center gap-2">
            <button
              onClick={openImport}
              className="bg-slate-800 hover:bg-slate-700 text-white border border-amber-500/40 hover:border-amber-500 px-4 py-2 rounded-lg font-bold flex items-center gap-2 transition-transform active:scale-95"
              data-testid="channels-import-m3u-btn"
              type="button"
            >
              <Upload size={18} /> M3U İçe Aktar
            </button>
            <button
              onClick={openAddModal}
              className="bg-amber-500 hover:bg-amber-600 text-black px-4 py-2 rounded-lg font-bold flex items-center gap-2 transition-transform active:scale-95"
              data-testid="channels-add-btn"
            >
              <Plus size={20} /> Yeni Kanal Ekle
            </button>
          </div>
        </div>

        {/* Bulk action toolbar — yalnızca seçim varken görünür */}
        {selected.size > 0 && (
          <div className="mb-4 bg-amber-500/10 border border-amber-500/40 rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="channels-bulk-toolbar">
            <span className="text-sm font-bold text-amber-400 flex items-center gap-2">
              <CheckSquare size={16} />
              {selected.size} kanal seçildi
            </span>
            <button
              onClick={() => setSelected(new Set())}
              className="text-xs text-gray-400 hover:text-white px-2 py-1 rounded transition flex items-center gap-1"
              data-testid="channels-bulk-clear"
              title="Seçimi temizle"
            >
              <X size={12} /> Temizle
            </button>
            <div className="flex-1" />
            <select
              onChange={e => { handleBulkSetCategory(e.target.value); e.target.value = ''; }}
              disabled={bulkBusy}
              defaultValue=""
              className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs text-white focus:border-amber-500 outline-none cursor-pointer disabled:opacity-50"
              data-testid="channels-bulk-set-category"
            >
              <option value="">Kategori değiştir…</option>
              {categories.map(cat => <option key={cat._id} value={cat._id}>{cat.name}</option>)}
            </select>
            <button
              onClick={handleBulkDelete}
              disabled={bulkBusy}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-lg transition flex items-center gap-1.5"
              data-testid="channels-bulk-delete"
            >
              <Trash2 size={14} /> {bulkBusy ? 'İşleniyor…' : 'Toplu Sil'}
            </button>
          </div>
        )}

        <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden shadow-2xl">
          <table className="w-full text-left text-white" data-testid="channels-table">
            <thead className="bg-slate-950 text-gray-400 text-xs uppercase font-bold tracking-wider">
              <tr>
                <th className="p-4 w-10 text-center">
                  <button
                    onClick={toggleAll}
                    className="text-gray-500 hover:text-amber-400 transition"
                    data-testid="channels-select-all"
                    title={allSelected ? 'Hiçbirini seçme' : 'Tümünü seç'}
                  >
                    {allSelected ? <CheckSquare size={18} className="text-amber-500" />
                     : someSelected ? <CheckSquare size={18} className="text-amber-500/60" />
                     : <Square size={18} />}
                  </button>
                </th>
                <th className="p-4 w-16 text-center">Logo</th>
                <th className="p-4 font-semibold text-white">Kanal / EPG</th>
                <th className="p-4 font-semibold text-white">Kategori</th>
                <th className="p-4 font-semibold text-white">Durum</th>
                <th className="p-4 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {channels.length === 0 ? (
                <tr>
                  <td colSpan="6" className="p-12 text-center text-gray-500 italic" data-testid="channels-empty">
                    Henüz kanal eklenmemiş. "Yeni Kanal Ekle" butonunu kullanın.
                  </td>
                </tr>
              ) : (
                channels.map(chan => (
                  <tr key={chan._id} className={`hover:bg-slate-800/30 transition-colors group ${selected.has(chan._id) ? 'bg-amber-500/5' : ''}`} data-testid={`channel-row-${chan._id}`}>
                    <td className="p-4 text-center">
                      <button
                        onClick={() => toggleOne(chan._id)}
                        className="text-gray-500 hover:text-amber-400 transition"
                        data-testid={`channel-select-${chan._id}`}
                        title="Seç"
                      >
                        {selected.has(chan._id) ? <CheckSquare size={18} className="text-amber-500" /> : <Square size={18} />}
                      </button>
                    </td>
                    <td className="p-4">
                      <div className="w-12 h-12 bg-slate-950 border border-slate-800 rounded-lg flex items-center justify-center overflow-hidden shadow-inner group-hover:border-amber-500/50 transition-colors">
                        {chan.logo_url ? <img src={chan.logo_url} alt={chan.name} className="w-full h-full object-contain" /> : <Tv size={20} className="text-slate-700" />}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="font-bold text-white flex items-center gap-2">
                        {chan.name}
                        {chan.is_protected ? <Shield size={14} className="text-amber-500" /> : <ShieldOff size={14} className="text-gray-600" />}
                      </div>
                      <div className="text-xs text-gray-500 font-mono flex items-center gap-3 mt-0.5 flex-wrap">
                        <span>EPG: {chan.epg_id || 'N/A'}</span>
                        <span className={chan.source_id ? 'text-amber-500/80' : 'text-gray-600'}>
                          • Src: {sources.find(s => s._id === chan.source_id)?.name || 'Yok'}
                        </span>
                        <span className={chan.transcode_profile_id ? 'text-emerald-500/80' : 'text-gray-600'}>
                          • Tx: {transcodes.find(t => t._id === chan.transcode_profile_id)?.name || 'Pass-through'}
                        </span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-xs px-2.5 py-1 bg-slate-800 text-slate-300 rounded-full border border-slate-700 font-medium">
                        {categories.find(cat => cat._id === chan.category_id)?.name || 'Kategorisiz'}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`flex items-center gap-1.5 text-[10px] font-bold uppercase ${chan.status === 'online' ? 'text-green-500' : 'text-red-500'}`}>
                        <div className={`w-1.5 h-1.5 rounded-full ${chan.status === 'online' ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                        {chan.status}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => togglePause(chan)}
                          className={`${chan.status === 'online' ? 'text-amber-400 hover:bg-amber-500/10' : 'text-green-400 hover:bg-green-500/10'} p-2 rounded-lg transition-all duration-200`}
                          data-testid={`channel-pause-btn-${chan._id}`}
                          title={chan.status === 'online' ? 'Duraklat' : 'Yayına al'}
                          type="button"
                        >
                          {chan.status === 'online' ? <Pause size={18} /> : <Play size={18} />}
                        </button>
                        <button
                          onClick={() => openEdit(chan)}
                          className="text-gray-500 hover:text-amber-400 hover:bg-amber-500/10 p-2 rounded-lg transition-all duration-200"
                          data-testid={`channel-edit-btn-${chan._id}`}
                          title="Kanalı düzenle"
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(chan._id)}
                          className="text-gray-500 hover:text-red-500 hover:bg-red-500/10 p-2 rounded-lg transition-all duration-200"
                          data-testid={`channel-delete-btn-${chan._id}`}
                          title="Kanalı sil"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {showAddModal && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[100] p-4 overflow-y-auto" data-testid="channels-add-modal">
            <div className="bg-slate-900 border border-slate-800 w-full max-w-xl rounded-2xl p-8 shadow-2xl animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto my-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-white flex items-center gap-2"><Tv className="text-amber-500" /> Yeni Kanal Ekle</h3>
                <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-white" data-testid="channels-add-modal-close">&times;</button>
              </div>
              <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1 md:col-span-2">
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">Kanal Adı</label>
                  <input
                    required
                    placeholder="Örn: Bein Sports 1"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none transition-all"
                    value={newChan.name}
                    onChange={e => setNewChan({ ...newChan, name: e.target.value })}
                    data-testid="channel-name-input"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">Kategori</label>
                  <select
                    required
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none appearance-none cursor-pointer"
                    value={newChan.category_id}
                    onChange={e => setNewChan({ ...newChan, category_id: e.target.value })}
                    data-testid="channel-category-select"
                  >
                    <option value="">Seçiniz...</option>
                    {categories.map(cat => <option key={cat._id} value={cat._id}>{cat.name}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">EPG Kaynağı</label>
                  <select
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none appearance-none cursor-pointer"
                    value={newChan.epg_source_id}
                    onChange={e => setNewChan({ ...newChan, epg_source_id: e.target.value })}
                    data-testid="channel-epg-source-select"
                  >
                    <option value="">Yok</option>
                    {epgSources.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">EPG ID (Kanal kodu)</label>
                  <input
                    placeholder="Örn: bein-sports-1"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none transition-all font-mono"
                    value={newChan.epg_id}
                    onChange={e => setNewChan({ ...newChan, epg_id: e.target.value })}
                    data-testid="channel-epg-input"
                  />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">Stream Source URL</label>
                  {sources.length > 0 && (
                    <select
                      className="w-full bg-slate-950 border border-amber-500/30 rounded-xl p-2.5 text-amber-300 text-xs font-mono focus:border-amber-500 outline-none cursor-pointer mb-1.5"
                      value=""
                      onChange={e => {
                        const s = sources.find(x => x._id === e.target.value);
                        if (s) setNewChan({ ...newChan, stream_url: s.url || '', source_id: s._id });
                      }}
                      data-testid="channel-stream-url-from-source"
                    >
                      <option value="">⚡ Yayın Kaynağından otomatik doldur…</option>
                      {sources.map(s => <option key={s._id} value={s._id}>{s.name} ({s.type})</option>)}
                    </select>
                  )}
                  <input
                    required
                    placeholder="http://source-ip:8080/live/... veya yukarıdan kaynak seç"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none transition-all font-mono"
                    value={newChan.stream_url}
                    onChange={e => setNewChan({ ...newChan, stream_url: e.target.value })}
                    data-testid="channel-stream-url-input"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">Birincil Kaynak</label>
                  <select
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none appearance-none cursor-pointer"
                    value={newChan.source_id}
                    onChange={e => setNewChan({ ...newChan, source_id: e.target.value })}
                    data-testid="channel-source-select"
                  >
                    <option value="">Yok</option>
                    {sources.map(s => <option key={s._id} value={s._id}>{s.name} ({s.type})</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">Transcode Profili</label>
                  <select
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none appearance-none cursor-pointer"
                    value={newChan.transcode_profile_id}
                    onChange={e => setNewChan({ ...newChan, transcode_profile_id: e.target.value })}
                    data-testid="channel-transcode-select"
                  >
                    <option value="">Pass-through</option>
                    {transcodes.map(t => <option key={t._id} value={t._id}>{t.name} · {t.codec} {t.resolution}</option>)}
                  </select>
                </div>
                <div className="space-y-1 md:col-span-2">
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">Logo URL (Opsiyonel)</label>
                  <input
                    placeholder="https://..."
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none transition-all"
                    value={newChan.logo_url}
                    onChange={e => setNewChan({ ...newChan, logo_url: e.target.value })}
                    data-testid="channel-logo-input"
                  />
                </div>
                <div className="flex gap-4 pt-4 md:col-span-2">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 bg-slate-800 text-white font-bold py-4 rounded-xl hover:bg-slate-700 transition-colors"
                    data-testid="channel-cancel-btn"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-amber-500 text-black font-bold py-4 rounded-xl hover:bg-amber-600 transition-colors shadow-lg shadow-amber-500/20"
                    data-testid="channel-save-btn"
                  >
                    Kaydet ve Yayınla
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ---- Edit Channel Modal ---- */}
        {editChan && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[100] p-4 overflow-y-auto" data-testid="channels-edit-modal">
            <div className="bg-slate-900 border border-amber-500/30 w-full max-w-xl rounded-2xl p-8 shadow-2xl shadow-amber-500/10 max-h-[90vh] overflow-y-auto my-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Pencil className="text-amber-500" /> Kanalı Düzenle
                </h3>
                <button
                  onClick={() => setEditChan(null)}
                  className="text-gray-400 hover:text-white"
                  data-testid="channels-edit-modal-close"
                  type="button"
                >
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleEditSave} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1 md:col-span-2">
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">Kanal Adı</label>
                  <input
                    required
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none"
                    value={editChan.name}
                    onChange={e => setEditChan({ ...editChan, name: e.target.value })}
                    data-testid="channel-edit-name"
                  />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">Yayın Linki (Stream URL)</label>
                  {sources.length > 0 && (
                    <select
                      className="w-full bg-slate-950 border border-amber-500/30 rounded-xl p-2.5 text-amber-300 text-xs font-mono focus:border-amber-500 outline-none cursor-pointer mb-1.5"
                      value=""
                      onChange={e => {
                        const s = sources.find(x => x._id === e.target.value);
                        if (s) setEditChan({ ...editChan, stream_url: s.url || '', source_id: s._id });
                      }}
                      data-testid="channel-edit-stream-url-from-source"
                    >
                      <option value="">⚡ Yayın Kaynağından otomatik doldur…</option>
                      {sources.map(s => <option key={s._id} value={s._id}>{s.name} ({s.type})</option>)}
                    </select>
                  )}
                  <input
                    required
                    placeholder="http://source-ip:8080/live/... veya yukarıdan kaynak seç"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none font-mono"
                    value={editChan.stream_url}
                    onChange={e => setEditChan({ ...editChan, stream_url: e.target.value })}
                    data-testid="channel-edit-stream-url"
                  />
                  <p className="text-[10px] text-gray-500 mt-1">
                    Bu link değiştirildiğinde, müşteri Player'ı bir sonraki kanal değişiminde otomatik yeni URL'yi kullanır.
                  </p>
                </div>
                <div className="space-y-1 md:col-span-2">
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">Logo URL</label>
                  <input
                    placeholder="https://..."
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none"
                    value={editChan.logo_url}
                    onChange={e => setEditChan({ ...editChan, logo_url: e.target.value })}
                    data-testid="channel-edit-logo-url"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">Kategori</label>
                  <select
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none cursor-pointer"
                    value={editChan.category_id}
                    onChange={e => setEditChan({ ...editChan, category_id: e.target.value })}
                    data-testid="channel-edit-category"
                  >
                    <option value="">Kategorisiz</option>
                    {categories.map(cat => <option key={cat._id} value={cat._id}>{cat.name}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">EPG ID</label>
                  <input
                    placeholder="bein-sports-1"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none font-mono"
                    value={editChan.epg_id}
                    onChange={e => setEditChan({ ...editChan, epg_id: e.target.value })}
                    data-testid="channel-edit-epg-id"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">EPG Kaynağı</label>
                  <select
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none cursor-pointer"
                    value={editChan.epg_source_id || ""}
                    onChange={e => setEditChan({ ...editChan, epg_source_id: e.target.value })}
                    data-testid="channel-edit-epg-source"
                  >
                    <option value="">Yok</option>
                    {epgSources.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">Birincil Kaynak</label>
                  <select
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none cursor-pointer"
                    value={editChan.source_id || ""}
                    onChange={e => setEditChan({ ...editChan, source_id: e.target.value })}
                    data-testid="channel-edit-source"
                  >
                    <option value="">Yok</option>
                    {sources.map(s => <option key={s._id} value={s._id}>{s.name} ({s.type})</option>)}
                  </select>
                </div>
                <div className="space-y-1 md:col-span-2">
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">Transcode Profili</label>
                  <select
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none cursor-pointer"
                    value={editChan.transcode_profile_id || ""}
                    onChange={e => setEditChan({ ...editChan, transcode_profile_id: e.target.value })}
                    data-testid="channel-edit-transcode"
                  >
                    <option value="">Pass-through</option>
                    {transcodes.map(t => <option key={t._id} value={t._id}>{t.name} · {t.codec} {t.resolution}</option>)}
                  </select>
                </div>

                {editErr && (
                  <div className="md:col-span-2 flex items-start gap-2 p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-xs" data-testid="channel-edit-error">
                    <X size={14} className="shrink-0 mt-0.5" /> <span>{editErr}</span>
                  </div>
                )}

                <div className="flex gap-4 pt-4 md:col-span-2">
                  <button
                    type="button"
                    onClick={() => setEditChan(null)}
                    className="flex-1 bg-slate-800 text-white font-bold py-4 rounded-xl hover:bg-slate-700 transition-colors"
                    data-testid="channel-edit-cancel"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={editBusy}
                    className="flex-1 bg-amber-500 text-black font-bold py-4 rounded-xl hover:bg-amber-600 transition-colors shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2 disabled:opacity-60"
                    data-testid="channel-edit-save"
                  >
                    {editBusy ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                    {editBusy ? "Kaydediliyor…" : "Değişiklikleri Kaydet"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ---- M3U Import Modal ---- */}
        {showImport && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[100] p-4 overflow-y-auto" data-testid="channels-import-modal">
            <div className="bg-slate-900 border border-amber-500/30 w-full max-w-2xl rounded-2xl p-7 shadow-2xl shadow-amber-500/10 max-h-[90vh] overflow-y-auto my-auto">
              <div className="flex justify-between items-center mb-5">
                <h3 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Upload className="text-amber-500" /> M3U / CSV İçe Aktar
                </h3>
                <button
                  onClick={() => setShowImport(false)}
                  className="text-gray-400 hover:text-white"
                  data-testid="channels-import-close"
                  type="button"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Mode tabs */}
              <div className="flex border border-slate-800 rounded-lg overflow-hidden mb-4">
                <button
                  type="button"
                  onClick={() => setImportMode("url")}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold transition ${
                    importMode === "url" ? "bg-amber-500/15 text-amber-400" : "bg-slate-950 text-gray-500 hover:text-white"
                  }`}
                  data-testid="channels-import-mode-url"
                >
                  <LinkIcon size={14} /> URL'den Yükle
                </button>
                <button
                  type="button"
                  onClick={() => setImportMode("content")}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold transition ${
                    importMode === "content" ? "bg-amber-500/15 text-amber-400" : "bg-slate-950 text-gray-500 hover:text-white"
                  }`}
                  data-testid="channels-import-mode-content"
                >
                  <FileText size={14} /> Dosya / Yapıştır
                </button>
              </div>

              <form onSubmit={handleImport} className="space-y-4">
                {importMode === "url" ? (
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase ml-1">M3U Playlist URL'si</label>
                    <input
                      type="url"
                      placeholder="https://example.com/playlist.m3u8 veya https://provider.com/get.php?username=..."
                      value={importUrl}
                      onChange={(e) => setImportUrl(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none font-mono text-sm"
                      data-testid="channels-import-url"
                      autoFocus
                    />
                    <p className="text-[10px] text-gray-500 mt-1">
                      Backend bu URL'yi kendisi indirecek. Public erişilebilir olmalı.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-bold text-gray-500 uppercase ml-1">M3U İçeriği</label>
                      <label className="cursor-pointer text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1">
                        <Upload size={12} /> .m3u / .m3u8 dosyası seç
                        <input type="file" accept=".m3u,.m3u8,text/*" className="hidden" onChange={handleFileUpload} data-testid="channels-import-file" />
                      </label>
                    </div>
                    <textarea
                      placeholder="#EXTM3U&#10;#EXTINF:-1 tvg-logo=&quot;...&quot; group-title=&quot;Spor&quot;,Bein Sports 1&#10;http://provider/stream/1.m3u8&#10;..."
                      value={importContent}
                      onChange={(e) => setImportContent(e.target.value)}
                      rows={10}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none font-mono text-xs"
                      data-testid="channels-import-content"
                    />
                    <p className="text-[10px] text-gray-500 mt-1">
                      Doğrudan yapıştırın veya yukarıdaki bağlantıyla dosya seçin (max 10 MB).
                    </p>
                  </div>
                )}

                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">
                    Varsayılan Kategori (group-title yoksa kullanılır)
                  </label>
                  <input
                    type="text"
                    value={importDefaultCat}
                    onChange={(e) => setImportDefaultCat(e.target.value)}
                    placeholder="İçe Aktarılanlar"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:border-amber-500 outline-none text-sm"
                    data-testid="channels-import-default-cat"
                  />
                </div>

                {/* Conflict policy notice */}
                <div className="flex items-start gap-2 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5 text-[11px] text-amber-200/90">
                  <Shield size={14} className="shrink-0 mt-0.5 text-amber-400" />
                  <span>
                    <strong>Çakışma politikası:</strong> Aynı isimde kanal varsa <span className="font-mono">stream_url</span> ve <span className="font-mono">logo_url</span> üzerine yazılır.
                    Yoksa yeni kayıt eklenir. Eksik kategori yoksa <span className="font-mono">{importDefaultCat || "İçe Aktarılanlar"}</span> kategorisi otomatik oluşturulur.
                  </span>
                </div>

                {importErr && (
                  <div className="flex items-start gap-2 p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-xs" data-testid="channels-import-error">
                    <X size={14} className="shrink-0 mt-0.5" /> <span>{importErr}</span>
                  </div>
                )}

                {importResult && (
                  <div className="grid grid-cols-4 gap-2 p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5" data-testid="channels-import-result">
                    <ImportStat label="Bulunan" value={importResult.parsed} accent="white" />
                    <ImportStat label="Eklenen" value={importResult.inserted} accent="emerald" />
                    <ImportStat label="Güncellenen" value={importResult.updated} accent="amber" />
                    <ImportStat label="Hatalı" value={importResult.failed} accent={importResult.failed > 0 ? "red" : "gray"} />
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowImport(false)}
                    className="flex-1 bg-slate-800 text-white font-bold py-3 rounded-xl hover:bg-slate-700 transition-colors"
                    data-testid="channels-import-cancel"
                  >
                    Kapat
                  </button>
                  <button
                    type="submit"
                    disabled={importBusy}
                    className="flex-1 bg-amber-500 text-black font-bold py-3 rounded-xl hover:bg-amber-600 transition-colors shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2 disabled:opacity-60"
                    data-testid="channels-import-submit"
                  >
                    {importBusy ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
                    {importBusy ? "İçe aktarılıyor…" : "İçe Aktar"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function ImportStat({ label, value, accent }) {
  const colorMap = {
    emerald: "text-emerald-400",
    amber:   "text-amber-400",
    red:     "text-red-400",
    white:   "text-white",
    gray:    "text-gray-400",
  };
  return (
    <div className="text-center">
      <div className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">{label}</div>
      <div className={`text-2xl font-bold mt-0.5 ${colorMap[accent] || "text-white"}`}>{value ?? 0}</div>
    </div>
  );
}
